import { createHttpService } from "../ApiService";
import {
  TCompliance,
  TCreateComplianceRequest,
  TUpdateComplianceRequest,
  TComplianceTemplate,
  TApplyTemplateRequest,
  TComplianceCategory,
  TComplianceListWireDto,
  TComplianceListItemWireDto,
  TComplianceStatus,
  TCreateComplianceItemBodyDto,
  TComplianceIdAckDto,
  TComplianceUpdateAckDto,
  COMPLIANCE_STATUSES,
} from "./compliance.interfaces";
import {
  complianceListMock,
  complianceTemplatesMock,
} from "../../__mocks__/compliance/complianceMock";
import { mergePaginationQuery } from "../common.interfaces";

const COMPLIANCE_CATEGORIES: TComplianceCategory[] = [
  "regulatory",
  "institutional",
  "accreditation",
  "other",
];

function parseCategoryFromWire(value: string): TComplianceCategory {
  const normalized = value?.toLowerCase() as TComplianceCategory;
  return COMPLIANCE_CATEGORIES.includes(normalized) ? normalized : "other";
}

function parseStatusFromWire(value: string): TComplianceStatus {
  const trimmed = value?.trim();
  if ((COMPLIANCE_STATUSES as readonly string[]).includes(trimmed)) {
    return trimmed as TComplianceStatus;
  }
  const legacy = trimmed?.toLowerCase();
  if (legacy === "active") return "COMPLIANT";
  if (legacy === "inactive") return "NON_COMPLIANT";
  return "PENDING";
}

function listItemWireToCompliance(
  item: TComplianceListItemWireDto,
  instituteId: string,
): TCompliance {
  return {
    id: item.id,
    name: item.title,
    category: parseCategoryFromWire(item.category),
    status: parseStatusFromWire(item.status),
    description: item.description,
    frequency: undefined,
    dueDate: item.dueDate,
    instituteId,
  };
}

function toCreateBody(
  request: TCreateComplianceRequest,
): TCreateComplianceItemBodyDto {
  const metadata =
    request.frequency !== undefined
      ? { frequency: request.frequency }
      : undefined;

  return {
    title: request.name,
    instituteId: request.instituteId,
    description: request.description,
    category: request.category,
    status: request.status,
    dueDate: request.dueDate,
    code: request.name.replace(/\s+/g, "-").slice(0, 64),
    metadata,
  };
}

function toUpdateBody(
  request: TUpdateComplianceRequest,
): Partial<TCreateComplianceItemBodyDto> {
  const body: Partial<TCreateComplianceItemBodyDto> = {};
  if (request.name !== undefined) body.title = request.name;
  if (request.category !== undefined) body.category = request.category;
  if (request.description !== undefined) body.description = request.description;
  if (request.status !== undefined) body.status = request.status;
  if (request.dueDate !== undefined) body.dueDate = request.dueDate;
  if (request.frequency !== undefined) {
    body.metadata = { frequency: request.frequency };
  }
  return body;
}

export const useComplianceService = () => {
  const httpAPI = createHttpService();

  const appWithMockData = import.meta.env.VITE_APP_WITH_MOCK_DATA === "true";

  const getCompliances = (instituteId?: string) => {
    if (appWithMockData) {
      if (instituteId) {
        return Promise.resolve(
          complianceListMock.filter(
            (compliance) => compliance.instituteId === instituteId,
          ),
        );
      }
      return Promise.resolve(complianceListMock);
    }

    const params = {
      ...mergePaginationQuery(),
      ...(instituteId ? { instituteId } : {}),
    };

    return httpAPI.compliance
      .get<TComplianceListWireDto>("", { params })
      .then((wire) =>
        wire.data.map((item) =>
          listItemWireToCompliance(item, instituteId ?? ""),
        ),
      );
  };

  const createCompliance = (
    request: TCreateComplianceRequest,
  ): Promise<{ id: string }> => {
    if (appWithMockData) {
      const newCompliance: TCompliance = {
        id: `compliance-${Date.now()}`,
        name: request.name,
        category: request.category,
        status: request.status || "PENDING",
        description: request.description,
        frequency: request.frequency,
        dueDate: request.dueDate,
        instituteId: request.instituteId,
      };
      complianceListMock.push(newCompliance);
      return Promise.resolve({ id: newCompliance.id });
    }

    return httpAPI.compliance
      .post<TComplianceIdAckDto>("", toCreateBody(request))
      .then((ack) => {
        const id = ack.data?.id;
        if (!id) {
          throw new Error("Create compliance succeeded but no id was returned");
        }
        return { id };
      });
  };

  const updateCompliance = (
    id: string,
    request: TUpdateComplianceRequest,
  ): Promise<void> => {
    if (appWithMockData) {
      const index = complianceListMock.findIndex(
        (compliance) => compliance.id === id,
      );
      if (index === -1) {
        return Promise.reject(new Error("Compliance not found"));
      }

      complianceListMock[index] = {
        ...complianceListMock[index],
        ...request,
      };
      return Promise.resolve();
    }

    return httpAPI.compliance
      .put<TComplianceUpdateAckDto>(`/${id}`, toUpdateBody(request))
      .then(() => undefined);
  };

  const deleteCompliance = (id: string) => {
    if (appWithMockData) {
      const index = complianceListMock.findIndex(
        (compliance) => compliance.id === id,
      );
      if (index === -1) {
        return Promise.reject(new Error("Compliance not found"));
      }
      complianceListMock.splice(index, 1);
      return Promise.resolve(void 0);
    }
    return httpAPI.compliance.delete<void>(`/${id}`);
  };

  const getTemplates = () => {
    if (appWithMockData) {
      return Promise.resolve(complianceTemplatesMock);
    }
    // No `/v1/settings/compliance/templates` (or similar) in OpenAPI yet.
    return Promise.resolve([] as TComplianceTemplate[]);
  };

  const applyTemplate = (request: TApplyTemplateRequest) => {
    if (appWithMockData) {
      const template = complianceTemplatesMock.find(
        (t) => t.id === request.templateId,
      );
      if (!template) {
        return Promise.reject(new Error("Template not found"));
      }

      const createdCompliances: TCompliance[] = [];
      template.compliances.forEach((templateCompliance) => {
        const customization = request.customizations?.[templateCompliance.name];
        const complianceData: TCreateComplianceRequest = {
          ...templateCompliance,
          ...customization,
          instituteId: request.instituteId,
        };

        const newCompliance: TCompliance = {
          id: `compliance-${Date.now()}-${Math.random()}`,
          name: complianceData.name,
          category: complianceData.category,
          status: complianceData.status || "PENDING",
          description: complianceData.description,
          frequency: complianceData.frequency,
          dueDate: complianceData.dueDate,
          instituteId: complianceData.instituteId,
        };
        complianceListMock.push(newCompliance);
        createdCompliances.push(newCompliance);
      });

      return Promise.resolve(createdCompliances);
    }

    return Promise.reject(
      new Error(
        "Applying compliance templates is only supported when mock data is enabled (no OpenAPI endpoint yet).",
      ),
    );
  };

  return {
    getCompliances,
    createCompliance,
    updateCompliance,
    deleteCompliance,
    getTemplates,
    applyTemplate,
  };
};
